import React, { useMemo, useEffect, useState } from 'react';
import Joyride, { CallBackProps, Step } from 'react-joyride';

type Role = 'customer' | 'tailor';

interface GuidedTourProps {
  role: Role;
  open: boolean;
  onClose: () => void;
}

const GuidedTour: React.FC<GuidedTourProps> = ({ role, open, onClose }) => {
  const [validSteps, setValidSteps] = useState<Step[]>([]);

  const allSteps: Step[] = useMemo(() => {
    if (role === 'customer') {
      return [
        {
          target: 'body',
          title: 'Welcome to MasterJi',
          content: 'This quick tour shows how to create an order, book appointments, and chat with your tailor.',
          disableBeacon: true,
          placement: 'center',
        },
        {
          target: '[data-tour="customer-new-order"]',
          title: 'Create a New Order',
          content: 'Click here to place a new order. You can choose a garment, provide details, and confirm the amount to pay.',
        },
        {
          target: '[data-tour="customer-appointments-section"]',
          title: 'Appointments',
          content: 'See your tailors and book future fittings here. Tailors you have ordered from show up automatically.',
        },
        {
          target: '[data-tour="customer-messaging-entry"]',
          title: 'Messages',
          content: 'Open messaging to chat with your tailor or support. You’ll also get notifications for new messages.',
        },
        {
          target: '[data-tour="notifications-bell"]',
          title: 'Notifications',
          content: 'Click here to view all notifications for your orders, appointments, and messages.',
        },
        {
          target: '[data-tour="user-settings"]',
          title: 'Profile & Settings',
          content: 'Manage preferences such as theme and notifications, and access your profile from here.',
        },
      ];
    }
    // Tailor tour
    return [
      {
        target: 'body',
        title: 'Welcome, Tailor',
        content: 'This tour shows your orders, customers, and where to update your public profile.',
        disableBeacon: true,
        placement: 'center',
      },
      {
        target: '[data-tour="tailor-orders"]',
        title: 'Your Orders',
        content: 'Manage current orders, track progress, and view timelines here.',
      },
      {
        target: '[data-tour="tailor-new-order"]',
        title: 'Create Orders',
        content: 'Start a new order for a customer from here.',
      },
      {
        target: '[data-tour="tailor-profile"]',
        title: 'Profile & Public Page',
        content: 'Customize your public profile (templates, photos, service radius) so customers can discover you.',
      },
      {
        target: '[data-tour="tailor-messaging-entry"]',
        title: 'Messages',
        content: 'Open messaging to chat with customers about their orders and fittings.',
      },
    ];
  }, [role]);

  // Filter out steps with non-existent targets
  useEffect(() => {
    const filteredSteps = allSteps.filter(step => {
      // Always include steps that target 'body' or don't have specific targets
      if (step.target === 'body' || !step.target) {
        return true;
      }
      
      // Check if the target element exists in the DOM
      const element = document.querySelector(step.target as string);
      return element !== null;
    });
    
    setValidSteps(filteredSteps);
  }, [allSteps, open]);

  const handleCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === 'finished' || status === 'skipped') {
      onClose();
    }
  };

  return (
    <Joyride
      steps={validSteps}
      run={open && validSteps.length > 0}
      continuous
      scrollToFirstStep
      showSkipButton
      showProgress
      disableOverlayClose={false}
      spotlightPadding={8}
      styles={{
        options: { primaryColor: '#2563eb', zIndex: 10000 },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        skip: 'Skip',
      }}
      callback={handleCallback}
    />
  );
};

export default GuidedTour;
